import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  useMediaQuery,
  useTheme,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
  LinearProgress,
  CircularProgress,
  Alert,
  Tab,
  Tabs,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Search,
  Send,
  TrendingUp,
  Favorite,
  Share,
  Download,
  Layers,
  MoreVert,
  ContentCopy,
  Preview,
  Lightbulb,
  Bolt,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Code,
  FormatQuote,
  Link as LinkIcon,
  Title,
  FormatListBulleted,
  FormatListNumbered,
  FormatClear,
} from '@mui/icons-material';
import { templateApi, TemplateApiError } from '../../services/templateApi';
import { useAuth } from '../../context/AuthContext';

/**
 * Professional Template Manager Component
 * Advanced template library with analytics, preview, and professional features
 */
const TemplateManager = ({
  templates = [],
  onCreateTemplate = () => {},
  onEditTemplate = () => {},
  onDeleteTemplate = () => {},
  onUseTemplate = () => {},
  onDuplicateTemplate = () => {},
  stats = {},
  mergeTags = [],
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { token, user } = useAuth();

  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterTab, setFilterTab] = useState(0);
  const [sortBy, setSortBy] = useState('recent');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dbTemplates, setDbTemplates] = useState([]);
  const bodyRef = useRef(null);
  const [templateForm, setTemplateForm] = useState({
    id: '',
    name: '',
    subject: '',
    body: '',
    category: 'general',
    description: '',
    tags: [],
    openRate: 0,
    clickRate: 0,
    conversionRate: 0,
  });

  // Load templates from database when component mounts or user logs in
  useEffect(() => {
    if (!token || !user) {
      setDbTemplates([]);
      return;
    }

    const loadTemplates = async () => {
      setIsLoading(true);
      try {
        const response = await templateApi.listTemplates({}, token);
        if (response && response.templates) {
          setDbTemplates(response.templates);
        } else {
          setDbTemplates([]);
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
        setDbTemplates([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadTemplates();
  }, [token, user]);

  // Mock analytics data
  const mockTemplateMetrics = useMemo(() => {
    const metrics = {};
    const allTemplates = [...templates, ...dbTemplates];
    allTemplates.forEach((t) => {
      metrics[t.id || t.template_id] = {
        uses: Math.floor(Math.random() * 50),
        opens: Math.floor(Math.random() * 1000),
        clicks: Math.floor(Math.random() * 500),
        conversions: Math.floor(Math.random() * 100),
        openRate: Math.floor(Math.random() * 60),
        clickRate: Math.floor(Math.random() * 40),
        conversionRate: Math.floor(Math.random() * 15),
        createdDate: new Date(Date.now() - Math.random() * 30 * 24 * 60 * 60 * 1000),
        lastUsed: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
        favorite: typeof t.isFavorite === 'boolean' ? t.isFavorite : Math.random() > 0.6,
      };
    });
    return metrics;
  }, [templates, dbTemplates]);

  // Filter templates - combine props templates with database templates
  const filteredTemplates = useMemo(() => {
    let list = [...templates, ...dbTemplates];
    
    // Remove duplicates by template_id or id
    const seen = new Set();
    list = list.filter((t) => {
      const key = t.template_id || t.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      list = list.filter((t) => {
        const text = `${t.name || ''} ${t.subject || ''} ${t.description || ''} ${(t.tags || []).join(' ')}`.toLowerCase();
        return text.includes(query);
      });
    }

    // Category filter
    if (categoryFilter !== 'all') {
      list = list.filter((t) => (t.category || 'general') === categoryFilter);
    }

    // Tab filter
    if (filterTab === 1) {
      // Favorites
      list = list.filter((t) => t.isFavorite || mockTemplateMetrics[t.id || t.template_id]?.favorite);
    } else if (filterTab === 2) {
      // Recently used
      list = list.sort((a, b) => (mockTemplateMetrics[b.id || b.template_id]?.lastUsed || 0) - (mockTemplateMetrics[a.id || a.template_id]?.lastUsed || 0)).slice(0, 10);
    } else if (filterTab === 3) {
      // Top performers
      list = list.sort((a, b) => (mockTemplateMetrics[b.id || b.template_id]?.openRate || 0) - (mockTemplateMetrics[a.id || a.template_id]?.openRate || 0)).slice(0, 10);
    }

    // Sort
    if (sortBy === 'name') {
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    } else if (sortBy === 'performance') {
      list.sort((a, b) => (mockTemplateMetrics[b.id || b.template_id]?.openRate || 0) - (mockTemplateMetrics[a.id || a.template_id]?.openRate || 0));
    } else if (sortBy === 'uses') {
      list.sort((a, b) => (mockTemplateMetrics[b.id || b.template_id]?.uses || 0) - (mockTemplateMetrics[a.id || a.template_id]?.uses || 0));
    } else {
      // recent
      list.sort((a, b) => (mockTemplateMetrics[b.id || b.template_id]?.createdDate || 0) - (mockTemplateMetrics[a.id || a.template_id]?.createdDate || 0));
    }

    return list;
  }, [templates, dbTemplates, searchQuery, filterTab, sortBy, categoryFilter, mockTemplateMetrics]);

  const handleOpenTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm(template);
    setPreviewOpen(true);
  };

  const handleEditTemplate = (template) => {
    setSelectedTemplate(template);
    setTemplateForm(template);
    setEditDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name || !templateForm.subject || !templateForm.body) {
      setSnackbar({ open: true, message: 'Template name, subject, and body are required', severity: 'error' });
      return;
    }

    if (!token || !user) {
      setSnackbar({ open: true, message: 'Please login to save templates', severity: 'error' });
      return;
    }

    setIsSaving(true);
    try {
      const response = await templateApi.saveTemplate(templateForm, token);
      
      setSnackbar({ 
        open: true, 
        message: templateForm.id ? 'Template updated successfully' : 'Template saved successfully',
        severity: 'success'
      });

      // Update database templates list with saved template
      setDbTemplates((prev) => {
        const exists = prev.find((t) => (t.template_id || t.id) === (response.template.template_id || response.template.id));
        if (exists) {
          return prev.map((t) => (t.template_id || t.id) === (response.template.template_id || response.template.id) ? response.template : t);
        }
        return [...prev, response.template];
      });

      // Call the parent handler to update local state
      if (templateForm.id) {
        onEditTemplate(response.template, { skipRemote: true });
      } else {
        onCreateTemplate(response.template, { skipRemote: true });
      }

      setEditDialogOpen(false);
      setTemplateForm({
        id: '',
        name: '',
        subject: '',
        body: '',
        category: 'general',
        description: '',
        tags: [],
      });
    } catch (error) {
      const message = error instanceof TemplateApiError 
        ? error.message 
        : 'Failed to save template';
      setSnackbar({ open: true, message, severity: 'error' });
      console.error('Template save error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const applyTemplateFormat = (format) => {
    const textarea = bodyRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateForm.body || '';
    const selectedText = text.substring(start, end) || 'text';

    let before = '', after = '';
    switch (format) {
      case 'bold':
        before = '**';
        after = '**';
        break;
      case 'italic':
        before = '*';
        after = '*';
        break;
      case 'underline':
        before = '<u>';
        after = '</u>';
        break;
      case 'code':
        before = '```';
        after = '```';
        break;
      case 'quote':
        before = '> ';
        break;
      case 'link':
        before = '[';
        after = '](url)';
        break;
      case 'heading':
        before = '## ';
        break;
      case 'bullet':
        before = '- ';
        break;
      case 'number':
        before = '1. ';
        break;
      case 'clear':
        setTemplateForm((prev) => ({ ...prev, body: '' }));
        return;
      default:
        return;
    }

    const newText = text.substring(0, start) + before + selectedText + after + text.substring(end);
    setTemplateForm((prev) => ({ ...prev, body: newText }));

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length);
    }, 0);
  };

  const insertMergeTag = (tag) => {
    const textarea = bodyRef.current;
    if (!textarea) {
      setTemplateForm((prev) => ({
        ...prev,
        body: (prev.body || '') + tag,
      }));
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = templateForm.body || '';
    const newText = text.substring(0, start) + tag + text.substring(end);
    setTemplateForm((prev) => ({ ...prev, body: newText }));
    setTimeout(() => {
      textarea.focus();
      const pos = start + tag.length;
      textarea.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleToggleFavorite = (template) => {
    const updated = { ...template, isFavorite: !template.isFavorite };
    onEditTemplate(updated, { skipRemote: true });
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!token || !user) {
      setSnackbar({ open: true, message: 'Please login to delete templates', severity: 'error' });
      return;
    }

    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await templateApi.deleteTemplate(templateId, token);
      
      // Remove from database templates
      setDbTemplates((prev) => prev.filter((t) => (t.template_id || t.id) !== templateId));
      
      setSnackbar({ 
        open: true, 
        message: 'Template deleted successfully',
        severity: 'success'
      });

      // Call the parent handler to update local state
      onDeleteTemplate(templateId, { skipRemote: true });
      setSelectedTemplate(null);
    } catch (error) {
      const message = error instanceof TemplateApiError 
        ? error.message 
        : 'Failed to delete template';
      setSnackbar({ open: true, message, severity: 'error' });
      console.error('Template delete error:', error);
    }
  };

  const handleDuplicate = (template) => {
    const copy = {
      ...template,
      id: `tmpl-${Date.now()}`,
      name: `${template.name || 'Template'} (Copy)`,
    };
    onCreateTemplate(copy);
    onDuplicateTemplate(copy);
  };

  const categories = ['general', 'promotional', 'newsletter', 'transactional', 'welcome', 'followup', 're-engagement'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2 }}>
      {/* ====== HEADER ====== */}
      <Card
        sx={{
          background: 'linear-gradient(135deg, rgba(102,126,234,0.1) 0%, rgba(118,75,162,0.1) 100%)',
          borderRadius: isMobile ? 1 : 2,
          borderLeft: '4px solid #667eea',
        }}
      >
        <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={1.5}
          >
            <Box>
              <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                <Layers sx={{ fontSize: 28, color: '#667eea' }} />
                <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight={800}>
                  Template Library Pro
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Design, preview, and manage reusable email templates with advanced analytics
              </Typography>
            </Box>
            <Button
              size={isMobile ? 'small' : 'medium'}
              variant="contained"
              startIcon={<Add />}
              onClick={() => {
                setTemplateForm({
                  id: '',
                  name: '',
                  subject: '',
                  body: '',
                  category: 'general',
                  description: '',
                  tags: [],
                });
                setEditDialogOpen(true);
              }}
              fullWidth={isMobile}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            >
              {isMobile ? 'New' : 'Create Template'}
            </Button>
          </Stack>

          {/* Stats */}
          <Grid container spacing={1} sx={{ mt: 1 }}>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, background: '#f5f5f5', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Total Templates</Typography>
                <Typography fontWeight={700} variant="h6">
                  {typeof stats.totalTemplates === 'number' ? stats.totalTemplates : templates.length}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, background: '#e3f2fd', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Avg Open Rate</Typography>
                <Typography fontWeight={700} variant="h6">
                  {typeof stats.avgOpenRate === 'number'
                    ? Math.round(stats.avgOpenRate)
                    : templates.length > 0
                      ? Math.round(
                          templates.reduce((sum, t) => sum + (mockTemplateMetrics[t.id]?.openRate || 0), 0) /
                            templates.length
                        )
                      : 0}
                  %
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, background: '#e8f5e9', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Total Uses</Typography>
                <Typography fontWeight={700} variant="h6">
                  {typeof stats.totalUses === 'number'
                    ? stats.totalUses
                    : templates.reduce((sum, t) => sum + (mockTemplateMetrics[t.id]?.uses || 0), 0)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Paper sx={{ p: 1.5, background: '#fff3e0', textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Favorites</Typography>
                <Typography fontWeight={700} variant="h6">
                  {typeof stats.favorites === 'number'
                    ? stats.favorites
                    : templates.filter((t) => t.isFavorite || mockTemplateMetrics[t.id]?.favorite).length}
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* ====== TOOLBAR ====== */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search templates, tags..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          fullWidth={isMobile}
          sx={{ flex: 1, minWidth: 200 }}
        />
        <TextField
          select
          size="small"
          label="Sort"
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          sx={{ minWidth: 150 }}
          SelectProps={{ native: true }}
        >
          <option value="recent">Most Recent</option>
          <option value="name">Name (A-Z)</option>
          <option value="performance">Top Performance</option>
          <option value="uses">Most Used</option>
        </TextField>
        <TextField
          select
          size="small"
          label="Category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          sx={{ minWidth: 160 }}
          SelectProps={{ native: true }}
        >
          <option value="all">All Categories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </option>
          ))}
        </TextField>
      </Stack>

      {/* ====== TABS ====== */}
      <Tabs
        value={filterTab}
        onChange={(e, newValue) => setFilterTab(newValue)}
        sx={{ borderBottom: '1px solid #eee', overflow: 'auto' }}
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label="All" icon={<Layers />} iconPosition="start" />
        <Tab label="Favorites" icon={<Favorite />} iconPosition="start" />
        <Tab label="Recently Used" icon={<TrendingUp />} iconPosition="start" />
        <Tab label="Top Performers" icon={<Bolt />} iconPosition="start" />
      </Tabs>

      {/* ====== TEMPLATES GRID ====== */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredTemplates.length === 0 ? (
        <Alert
          severity="info"
          icon={<Lightbulb />}
          sx={{
            background: 'linear-gradient(135deg, rgba(33,150,243,0.1) 0%, rgba(33,150,243,0.05) 100%)',
          }}
        >
          <Typography fontWeight={700}>No templates found</Typography>
          <Typography variant="caption">Create a new template to get started</Typography>
        </Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredTemplates.map((template) => {
            const baseMetrics = mockTemplateMetrics[template.id] || {};
            const metrics = {
              ...baseMetrics,
              uses: template.uses ?? baseMetrics.uses,
              opens: template.opens ?? baseMetrics.opens,
              clicks: template.clicks ?? baseMetrics.clicks,
              conversions: template.conversions ?? baseMetrics.conversions,
              openRate: template.openRate ?? baseMetrics.openRate,
              clickRate: template.clickRate ?? baseMetrics.clickRate,
              conversionRate: template.conversionRate ?? baseMetrics.conversionRate,
            };
            const isFavorite = typeof template.isFavorite === 'boolean' ? template.isFavorite : metrics.favorite;

            return (
              <Grid item xs={12} sm={isMobile ? 12 : 6} md={4} key={template.id}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    border: isFavorite ? '2px solid #667eea' : '1px solid #eee',
                    backgroundColor: isFavorite ? 'rgba(102,126,234,0.05)' : '#fff',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                      transform: 'translateY(-4px)',
                    },
                  }}
                >
                  {/* Header with Badge */}
                  <Box sx={{
                    p: 1.5,
                    background: isFavorite ? 'linear-gradient(135deg, #667eea20 0%, #764ba220 100%)' : '#f9f9f9',
                    borderBottom: '1px solid #eee',
                    position: 'relative',
                  }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                      <Box sx={{ flex: 1, pr: 1 }}>
                        <Typography fontWeight={700} variant="body2" noWrap>
                          {template.name || 'Untitled Template'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {template.subject || 'No subject'}
                        </Typography>
                      </Box>
                      <Tooltip title={isFavorite ? 'Remove favorite' : 'Add favorite'}>
                        <IconButton size="small" onClick={() => handleToggleFavorite(template)}>
                          <Favorite sx={{ fontSize: 20, color: isFavorite ? '#667eea' : '#ccc' }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {template.category && (
                        <Chip
                          size="small"
                          label={template.category}
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      )}
                      {(template.tags || []).slice(0, 2).map((tag) => (
                        <Chip key={tag} size="small" label={tag} variant="filled" sx={{ fontSize: '0.7rem' }} />
                      ))}
                    </Stack>
                  </Box>

                  {/* Analytics */}
                  <Box sx={{ p: 1.5, flex: 1 }}>
                    <Typography fontWeight={700} variant="body2" mb={1}>
                      Performance
                    </Typography>
                    <Grid container spacing={1} mb={1.5}>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 1, textAlign: 'center', background: '#e3f2fd' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Open Rate
                          </Typography>
                          <Typography fontWeight={700} sx={{ color: '#2196F3' }}>
                            {metrics.openRate || 0}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper sx={{ p: 1, textAlign: 'center', background: '#e8f5e9' }}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Click Rate
                          </Typography>
                          <Typography fontWeight={700} sx={{ color: '#4CAF50' }}>
                            {metrics.clickRate || 0}%
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={12}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="caption" fontWeight={700}>
                            Engagement
                          </Typography>
                          <Typography variant="caption">
                            {metrics.openRate || 0}%
                          </Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate"
                          value={metrics.openRate || 0}
                          sx={{ height: 6, borderRadius: 1 }}
                        />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 1 }} />

                    <Stack spacing={1} sx={{ fontSize: '0.85rem' }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Uses:</Typography>
                        <Typography fontWeight={700}>{metrics.uses || 0}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Total Opens:</Typography>
                        <Typography fontWeight={700}>{metrics.opens || 0}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Total Clicks:</Typography>
                        <Typography fontWeight={700}>{metrics.clicks || 0}</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography color="text.secondary">Conversions:</Typography>
                        <Typography fontWeight={700} sx={{ color: '#4CAF50' }}>
                          {metrics.conversions || 0}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Actions */}
                  <Stack direction="row" spacing={1} sx={{ p: 1.5, borderTop: '1px solid #eee' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Preview />}
                      onClick={() => handleOpenTemplate(template)}
                      fullWidth
                    >
                      Preview
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<Send />}
                      onClick={() => onUseTemplate(template)}
                      fullWidth
                    >
                      Use
                    </Button>
                    <Tooltip title="More options">
                      <IconButton size="small">
                        <MoreVert fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>

                  {/* Quick Actions */}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{
                      p: 1,
                      borderTop: '1px solid #eee',
                      bgcolor: '#fafafa',
                      justifyContent: 'center',
                    }}
                  >
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEditTemplate(template)}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                      <IconButton
                        size="small"
                        onClick={() => handleDuplicate(template)}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton size="small">
                        <Download fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share">
                      <IconButton size="small">
                        <Share fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTemplate(template.id)}
                        color="error"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* ====== PREVIEW DIALOG ====== */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{selectedTemplate?.name || 'Template Preview'}</DialogTitle>
        <DialogContent>
          {selectedTemplate && (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Box>
                <Typography fontWeight={700} variant="body2" mb={1}>
                  Subject
                </Typography>
                <Paper sx={{ p: 2, background: '#f5f5f5' }}>
                  <Typography>{selectedTemplate.subject}</Typography>
                </Paper>
              </Box>
              <Box>
                <Typography fontWeight={700} variant="body2" mb={1}>
                  Preview
                </Typography>
                <Paper sx={{ p: 2, background: '#fff', border: '1px solid #ddd', minHeight: 300 }}>
                  <Typography sx={{ whiteSpace: 'pre-wrap' }}>{selectedTemplate.body}</Typography>
                </Paper>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          <Button
            variant="contained"
            startIcon={<Send />}
            onClick={() => {
              onUseTemplate(selectedTemplate);
              setPreviewOpen(false);
            }}
          >
            Use This Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* ====== EDIT DIALOG ====== */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {templateForm.id ? 'Edit Template' : 'Create New Template'}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Template Name"
              fullWidth
              value={templateForm.name}
              onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={2}
              value={templateForm.description}
              onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
            />
            <TextField
              label="Tags (comma separated)"
              fullWidth
              value={(templateForm.tags || []).join(', ')}
              onChange={(e) =>
                setTemplateForm({
                  ...templateForm,
                  tags: e.target.value
                    .split(',')
                    .map((tag) => tag.trim())
                    .filter(Boolean),
                })
              }
              placeholder="e.g., onboarding, promo, follow-up"
            />
            <TextField
              select
              label="Category"
              fullWidth
              value={templateForm.category}
              onChange={(e) => setTemplateForm({ ...templateForm, category: e.target.value })}
              SelectProps={{ native: true }}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </TextField>
            <TextField
              label="Email Subject"
              fullWidth
              value={templateForm.subject}
              onChange={(e) => setTemplateForm({ ...templateForm, subject: e.target.value })}
              placeholder="e.g., Welcome to our platform!"
            />

            <Box>
              <Typography fontWeight={700} variant="body2" mb={1}>
                Email Body
              </Typography>
              <Paper sx={{ borderBottom: '1px solid #eee' }}>
                <Stack direction="row" spacing={0.5} sx={{ p: 1, flexWrap: 'wrap', gap: 0.5 }}>
                  {[
                    { icon: <FormatBold fontSize="small" />, label: 'Bold', action: 'bold' },
                    { icon: <FormatItalic fontSize="small" />, label: 'Italic', action: 'italic' },
                    { icon: <FormatUnderlined fontSize="small" />, label: 'Underline', action: 'underline' },
                    { icon: <Code fontSize="small" />, label: 'Code', action: 'code' },
                    { icon: <FormatQuote fontSize="small" />, label: 'Quote', action: 'quote' },
                    { icon: <LinkIcon fontSize="small" />, label: 'Link', action: 'link' },
                    { icon: <Title fontSize="small" />, label: 'Heading', action: 'heading' },
                    { icon: <FormatListBulleted fontSize="small" />, label: 'Bullet', action: 'bullet' },
                    { icon: <FormatListNumbered fontSize="small" />, label: 'Number', action: 'number' },
                    { icon: <FormatClear fontSize="small" />, label: 'Clear', action: 'clear' },
                  ].map((btn) => (
                    <Tooltip key={btn.action} title={btn.label}>
                      <IconButton
                        size="small"
                        onClick={() => applyTemplateFormat(btn.action)}
                      >
                        {btn.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Stack>
              </Paper>
              <TextField
                fullWidth
                multiline
                minRows={8}
                name="template-body"
                value={templateForm.body}
                onChange={(e) => setTemplateForm({ ...templateForm, body: e.target.value })}
                placeholder="Write your email template here..."
                inputRef={bodyRef}
              />
              <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={1}>
                {(mergeTags.length
                  ? mergeTags
                  : ['{{first_name}}', '{{company}}', '{{email}}', '{{city}}', '{{phone}}', '{{custom_field}}']
                ).map((tag) => (
                  <Chip
                    key={tag}
                    size="small"
                    label={tag}
                    onClick={() => insertMergeTag(tag)}
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveTemplate}
            disabled={isSaving}
            startIcon={templateForm.id ? <Edit /> : <Add />}
          >
            {isSaving ? 'Saving...' : (templateForm.id ? 'Save Changes' : 'Create Template')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TemplateManager;
